//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import {
  CreateYouTubePlaylistRequest,
  GetYouTubePlaylistsRequest,
  YouTubePaginatedPlaylists,
  YouTubePlaylist,
  YouTubeUser,
  YouTubeVideoCategory,
} from '@apis/youtube/youtubeApiModel';
import { YouTubeVisibility } from '@models/YouTubeVisibility';
import { isDefined } from '@utils/filterPredicates';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const YOUTUBE_DEFAULT_ENDPOINT = '/api/v1';

const youtubeGatewayFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'youtube_gateway',
    `${YOUTUBE_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );
};

/**
 * Gets the client's default connected YouTube account
 *
 * GET /api/v1/user
 */
export const getYouTubeUser = async (
  init?: RequestInit,
): Promise<YouTubeUser> => {
  const res = await youtubeGatewayFetch(`/user`, init);

  return (await res.json<{ data: YouTubeUser }>()).data;
};

/**
 * Gets playlists for the connected youtube account
 *
 * GET /api/v1/playlist
 */
export const getYouTubePlaylists = async (
  req: GetYouTubePlaylistsRequest,
  init?: RequestInit,
): Promise<YouTubePaginatedPlaylists> => {
  const params = makeParams(req);
  const res = await youtubeGatewayFetch(`/playlist?${params}`, init);

  return (await res.json<{ data: YouTubePaginatedPlaylists }>()).data;
};

/**
 * Gets video categories for the connected youtube account
 *
 * GET /api/v1/video/categories
 */
export const getYouTubeVideoCategories = async (
  init?: RequestInit,
): Promise<YouTubeVideoCategory[]> => {
  const res = await youtubeGatewayFetch(`/video/categories`, init);

  return (await res.json<{ data: YouTubeVideoCategory[] }>()).data;
};

/**
 * Creates playlist for the connected youtube account
 *
 * POST /api/v1/playlist
 */
export const createYouTubePlaylist = async (
  body: CreateYouTubePlaylistRequest,
  init?: RequestInit,
): Promise<YouTubePlaylist> => {
  const res = await youtubeGatewayFetch(`/playlist`, {
    ...(isDefined(init) ? init : {}),
    body: JSON.stringify(body),
    method: 'POST',
  });

  return (await res.json<{ data: YouTubePlaylist }>()).data;
};
